/** Session keeper — SILENT refresh in the background using the long-lived
 * refresh token. The old "session expiring" toast now only appears if the
 * silent refresh itself fails (revoked/expired refresh token). */
import React, { useEffect, useState, useCallback } from 'react';
import { trySilentRefresh } from './authRefresh';

const TOKEN_KEY = 'kindra-admin-token';

const tokenExpMs = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return null;
  try {
    const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (p.exp || 0) * 1000;
  } catch { return null; }
};

export const SessionKeeper = () => {
  const [warn, setWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');

  useEffect(() => {
    let dead = false;
    const tick = async () => {
      const exp = tokenExpMs();
      const left = exp ? exp - Date.now() : 0;
      if (left < 2 * 60 * 60 * 1000) {
        // < 2h left (or missing) → refresh silently, no interruption
        const ok = await trySilentRefresh();
        if (dead) return;
        if (ok) { setWarn(false); return; }
        // silent refresh unavailable → fall back to the visible toast
        setWarn(left > 0 && left < 5 * 60 * 1000);
      } else {
        setWarn(false);
      }
    };
    tick();
    const iv = setInterval(tick, 10 * 60 * 1000);
    return () => { dead = true; clearInterval(iv); };
  }, []);

  const extend = useCallback(async () => {
    setBusy(true);
    const ok = await trySilentRefresh();
    if (ok) {
      setWarn(false);
      setFlash('✓ Signed back in');
      setTimeout(() => setFlash(''), 4000);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      window.location.assign('/admin/login?returnTo=' + encodeURIComponent(window.location.pathname));
    }
    setBusy(false);
  }, []);

  if (!warn && !flash) return null;
  return (
    <div data-testid="session-keeper-toast" style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 90, display: 'flex', gap: 10,
      alignItems: 'center', background: '#181040', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 14, padding: '10px 16px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
      {flash ? (
        <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600, color: '#00E5C3' }}>{flash}</span>
      ) : (
        <>
          <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600, color: '#F4F1FF' }}>
            ⏳ Session expiring soon
          </span>
          <button data-testid="session-extend-btn" onClick={extend} disabled={busy}
            style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 800, color: '#0B0526',
                     background: 'linear-gradient(90deg,#00E5C3,#38B6FF)', border: 'none',
                     borderRadius: 999, padding: '5px 14px', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? '…' : 'Stay signed in'}
          </button>
        </>
      )}
    </div>
  );
};
